'use client';

import { useEffect, useMemo, useRef, useState, memo } from 'react';
import clsx from 'clsx';
import { DataTestConfig, ThirdPartSelectors } from '@headless-workspace/config';
import {
    BACK_TO_TOP_ID,
    UIRedDotInformation,
    useLayoutActionContext,
    useTailwindBreakpoint,
} from '@headless-workspace/core-ui';
import {
    ProductType,
    SelectorAddToBasketButton,
    SelectorAddToBasketButtonContext,
    useProductDetailContext,
    VATLegalMention,
    WishlistState,
} from '@headless-workspace/domain/common/client';
import {
    CapacityFormat,
    ProductDetailVariantValue,
    ProductSetItemValue,
    VipBrandsValue,
} from '@headless-workspace/domain/common/server';
import {
    GlowImage,
    GlowPrice,
    GlowPriceProps,
    GlowSkeleton,
    GlowTextBodySmall,
    PropsWithLocale,
} from '@headless-workspace/glow-ds';
import { ComponentMemo, Image } from '@headless-workspace/typings';
import { StringHelpers } from '@headless-workspace/utils';
import { SelectorWishlistButton } from './SelectorWishlistButton';
import { VariantSelector } from './VariantSelector';
import { VariantSelectorZone } from './VariantSelectorZone';

export type ProductDetailSelectorContainerProps = GlowPriceProps &
    PropsWithLocale & {
        productName?: string;
        productImage?: Image;
        brandName: string;
        vipBrands: VipBrandsValue;
        isVipBrand: boolean;
        type: ProductType;
        formats: CapacityFormat[];
        variants: ProductDetailVariantValue[];
        currency: string;
        stock: number;
        squaredShade: boolean;
        isRedDot?: boolean;
        redDotText?: string;
        productsSetList?: ProductSetItemValue[];
        setHoveredVariant?: (variantId: string) => void;
        isPriceLegalMentionEnabled: boolean;
    };

const IMAGE_WIDTH = 40;
const IMAGE_HEIGHT = 40;
const BACK_TO_TOP_OFFSET = 16;

const STATIC_CTA_ID = 'add-to-basket-cta';
const FIXED_ELEMENT_ID = 'add-to-basket-fixed';

export const ProductDetailSelectorContainer: ComponentMemo<ProductDetailSelectorContainerProps> = memo(
    ({
        productName,
        productImage,
        brandName,
        locale,
        vipBrands,
        isVipBrand = false,
        fontFamily,
        type,
        formats,
        variants,
        currency,
        squaredShade,
        isRedDot,
        redDotText,
        stock,
        productsSetList,
        setHoveredVariant,
        isPriceLegalMentionEnabled,
        ...priceProps
    }) => {
        const isMinTablet = useTailwindBreakpoint('tablet');
        const [isCTAVisible, setIsCTAVisible] = useState<boolean>(true);
        const {
            fixedAddToBasket: { setFixedAddToBasketHeight, setFixedAddToBasketVisible },
        } = useLayoutActionContext();

        const { productDetails, setProductVariantId, selectedVariantIds, productsSetAvailability } =
            useProductDetailContext();

        // Represent the product identifier. In the case of PRODUCT_SET, this will be the PRODUCT_SET id, in other cases, it will be the sku.
        const productId =
            productDetails.type !== ProductType.PRODUCT_SET ? productDetails.sku : productDetails.productSetId;

        // Represents the parent identifier of a product. In the case of PRODUCT_SET, this will be the PRODUCT_SET id, in other cases, it will be the masterId.
        const parentId =
            productDetails.type !== ProductType.PRODUCT_SET ? productDetails.masterId : productDetails.productSetId;

        const fixedAddToBasketRef = useRef<HTMLElement>(null);
        const [shouldDisplayWishlistButton, setShouldDisplayWishlistButton] = useState<WishlistState>('loading');

        // Observe the CTA to show the fixed bar
        useEffect(() => {
            const addToBasketCtaElement = document.getElementById(STATIC_CTA_ID);

            const addToBasketCtaObserver = new IntersectionObserver(([entry]) => {
                setIsCTAVisible(entry.isIntersecting);
                setFixedAddToBasketVisible(!entry.isIntersecting);
            });

            if (addToBasketCtaElement) {
                addToBasketCtaObserver.observe(addToBasketCtaElement);
            }

            return () => {
                addToBasketCtaObserver.disconnect();
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        // Move the UIBackToTop above the fixed bar when it is visible
        useEffect(() => {
            const backToTopElement = document.getElementById(BACK_TO_TOP_ID);

            if (backToTopElement) {
                const fixedElement = fixedAddToBasketRef.current;
                if (!isCTAVisible && fixedElement) {
                    backToTopElement.style.bottom = `${fixedElement.getBoundingClientRect().height + BACK_TO_TOP_OFFSET}px`;
                } else {
                    backToTopElement.style.removeProperty('bottom');
                }
            }
        }, [isCTAVisible]);

        // Adjust body padding when fixed element is visible to avoid footer to be hidden by fixed bar
        useEffect(() => {
            const fixedElement = fixedAddToBasketRef.current;

            if (fixedElement) {
                const bannerHeight = fixedElement.getBoundingClientRect().height;
                document.body.style.paddingBottom = `${bannerHeight}px`;
            }
        }, [isCTAVisible]);

        useEffect(() => {
            setFixedAddToBasketHeight(fixedAddToBasketRef?.current?.getBoundingClientRect().height ?? 0);
        }, [isCTAVisible, setFixedAddToBasketHeight]);

        const addToBasketCta = useMemo(() => {
            return (
                <SelectorAddToBasketButton
                    productType={type}
                    hasShortLabel={!isMinTablet && !isCTAVisible}
                    vipBrands={vipBrands}
                    locale={locale}
                    fontFamily={fontFamily}
                    isVisible={isCTAVisible}
                    setShouldDisplayWishlistButton={setShouldDisplayWishlistButton}
                    productsToAdd={selectedVariantIds}
                    productsSetAvailability={productsSetAvailability}
                    context={SelectorAddToBasketButtonContext.Page}
                />
            );
        }, [
            type,
            isMinTablet,
            isCTAVisible,
            vipBrands,
            locale,
            fontFamily,
            selectedVariantIds,
            productsSetAvailability,
        ]);

        const isProductSet = productDetails.type === ProductType.PRODUCT_SET;

        return (
            <>
                <div
                    id={`selector-zone`}
                    className={clsx('flex flex-col gap-1', ThirdPartSelectors.PurchaseInAppDescriptionClass)}
                >
                    <VariantSelectorZone
                        productName={productName}
                        type={type}
                        variants={variants}
                        formats={formats}
                        currency={currency}
                        enableDiscountInfo={priceProps.enableDiscountInfo}
                        priceAlwaysBlack={priceProps.priceAlwaysBlack ?? false}
                        isVipBrand={isVipBrand}
                        squaredShade={squaredShade}
                        locale={locale}
                        fontFamily={fontFamily}
                        productsSetList={productsSetList}
                        setHoveredVariant={setHoveredVariant}
                        onVariantSelected={setProductVariantId}
                        // We can use productId here because it is not used in case of a PRODUCT_SET
                        selectedVariantId={productId}
                        isPriceLegalMentionEnabled={isPriceLegalMentionEnabled}
                    />
                    <div id={STATIC_CTA_ID} className={'flex flex-row gap-0.5 w-full'}>
                        {addToBasketCta}
                        {shouldDisplayWishlistButton === 'loading' ? (
                            <GlowSkeleton width={'3rem'} height={'3rem'} />
                        ) : (
                            shouldDisplayWishlistButton === 'displayed' && (
                                <SelectorWishlistButton
                                    productId={productId}
                                    parentId={parentId}
                                    productName={productName}
                                    locale={locale}
                                />
                            )
                        )}
                    </div>
                    {isRedDot && redDotText && <UIRedDotInformation redDotText={redDotText} />}
                </div>
                {!isCTAVisible && (
                    <section
                        ref={fixedAddToBasketRef}
                        id={FIXED_ELEMENT_ID}
                        data-testid={DataTestConfig.testIds.addToBasketFixed}
                        className={clsx(
                            'fixed bottom-0 left-0 w-full z-addToBasketSticky',
                            'flex justify-center',
                            'bg-background-l0',
                            'border-solid border-t-medium border-border-primary shadow-stickyBottom',
                        )}
                    >
                        <div
                            className={clsx(
                                'max-w-full desktop:max-w-largestDesktop',
                                'px-1 py-0.75 desktopS:p-1 desktop:p-1.5',
                                'flex-1 flex flex-col gap-0.5',
                                isProductSet
                                    ? 'tablet:flex-row tablet:justify-between tablet:gap-1.5'
                                    : 'desktopS:flex-row desktopS:justify-between desktopS:gap-1.5',
                            )}
                        >
                            <div
                                className={clsx(
                                    'desktopS:w-fixedAddToCartDesktopS desktop:w-fixedAddToCartDesktop tablet:py-0 flex justify-between gap-1 items-center',
                                    isProductSet ? 'tablet:flex-1' : 'desktopS:flex-1',
                                )}
                            >
                                <div className={'flex justify-start gap-1 items-center min-w-[0px]'}>
                                    {productImage && (
                                        <GlowImage
                                            image={productImage}
                                            width={IMAGE_WIDTH}
                                            height={IMAGE_HEIGHT}
                                            className={'hidden desktopS:flex'}
                                            imgClassName={'whitespace-nowrap text-ellipsis'}
                                        />
                                    )}
                                    <div className={'whitespace-nowrap overflow-hidden'}>
                                        <GlowTextBodySmall
                                            text={brandName}
                                            fontFamily={fontFamily}
                                            className={
                                                'h-fixedAddToCartBrandName leading-6 overflow-hidden text-ellipsis'
                                            }
                                        />
                                        <GlowTextBodySmall
                                            text={productName}
                                            fontFamily={fontFamily}
                                            fontWeight={'bold'}
                                            className={'overflow-hidden text-ellipsis'}
                                        />
                                    </div>
                                </div>
                                <div className={'flex flex-col text-right'}>
                                    <GlowPrice
                                        fontFamily={fontFamily}
                                        {...priceProps}
                                        price={
                                            isPriceLegalMentionEnabled
                                                ? StringHelpers.addAsterisk(priceProps.price)
                                                : priceProps.price
                                        }
                                        className={'items-end'}
                                    />
                                    {isPriceLegalMentionEnabled && <VATLegalMention isClickable={false} />}
                                </div>
                            </div>
                            <div className={clsx('flex gap-0.5 h-fixedAddToCart', !isProductSet && 'tablet:flex-1')}>
                                <VariantSelector
                                    displayThumbnail={false}
                                    type={type}
                                    variants={variants}
                                    formats={formats}
                                    currency={currency}
                                    enableDiscountInfo={priceProps.enableDiscountInfo}
                                    priceAlwaysBlack={priceProps.priceAlwaysBlack ?? false}
                                    squaredShade={squaredShade}
                                    locale={locale}
                                    fontFamily={fontFamily}
                                    isVipBrand={isVipBrand}
                                    onVariantSelected={setProductVariantId}
                                    isPriceLegalMentionEnabled={isPriceLegalMentionEnabled}
                                    // We can use productId here because it is not used in case of a PRODUCT_SET
                                    selectedVariantId={productId}
                                />
                                {addToBasketCta}
                            </div>
                        </div>
                    </section>
                )}
            </>
        );
    },
);

ProductDetailSelectorContainer.displayName = 'ProductDetailSelectorContainer';
